import { React, useLayoutEffect, useState } from "react";
import {
  Typography,
  FormGroup,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Lottie from "react-lottie";
import AppButton from "../../base/AppButton.component";
import VerificationLayout from "../../layout/VerificationLayout.component";
import NoVehicle from "../../../assets/gifs/no-vehicle.json";
import {
  firstOfMonth,
  lottieLoopOptions,
  today,
  TYPE,
} from "../../../utils/Utils";
import { useVerification } from "../../../context/Verification.context";
import { useAuth } from "../../../context/Auth.context";
import { useMutation, useQuery } from "@apollo/client";
import { FETCH_TRADE_VEHICLE } from "../../../graphql/query/query";
import { SUBMIT_VEHICLE_TRADE } from "../../../graphql/mutation/mutation";
import { BulletListShimmer } from "react-content-shimmer";
import InformationDialog from "../../base/dialog-body/Information.dialog";
import HocPopup from "../../../hoc/hocPopup";
import { useAppContext } from "../../../context/App.context";
import { ArtifactStyle } from "./document/artifactResponse/VerficationArtifactsStyles";
import ToggleComponent from "../heygenAI/Toggle.component";
import { WalkthroughStyle } from "./WalkthroughDialogStyles";
import FetchAIVideoUrl from "../heygenAI/FetchAIVideoUrl";
import TextUnAvailable from "./document/artifactResponse/TextUnAvailable";

const TradeVehicleComponent = ({ dialog }) => {
  const navigate = useNavigate();
  const verification = useVerification();
  const auth = useAuth();
  const app = useAppContext();

  const [tradeVehicleIds, setTradeVehicleIds] = useState([]);
  const [tradeVehicles, setTradeVehicles] = useState([]);
  const [newVehicle, setNewVehicle] = useState({
    vin: "",
    year: "",
    make: "",
    model: "",
  });

  const [animateIn, setAnimateIn] = useState(false);
  const [duration, setDuration] = useState(0);
  const [direction, setDirection] = useState(null);
  const [showSurveyButton, setShowSurveyButton] = useState(false);

  lottieLoopOptions.animationData = NoVehicle;

  const [submitVehicleTrade, { loading: submitLoading }] = useMutation(
    SUBMIT_VEHICLE_TRADE,
    {
      onCompleted: (data) => {
        if (data?.submitTradeVehicle.employee_user_id) {
          navigate(`/verification/requestQuote`, { replace: true });
        } else {
          console.log("error");
        }
      },
    }
  );

  const { loading } = useQuery(FETCH_TRADE_VEHICLE, {
    variables: {
      tradeInput: {
        employee_user_id: parseInt(verification.employeeId),
        user_id: verification.customer.userId,
        insurance_id: verification?.customer?.insurance_id,
        dealership_id: parseInt(verification.dealershipId),
      },
    },
    onCompleted: async (data) => {
      if (data && data?.fetchTradeVehicle?.errors > 0) {
        dialog({
          open: true,
          bodyType: TYPE.DEFAULT,
          title: "Error",
          body: (
            <InformationDialog
              text={"Unable to fetch vehicle. Please try again"}
            />
          ),
        });
      } else {
        data?.fetchTradeVehicle?.vehicle?.forEach((vehicle) => {
          if (vehicle.vehicle_status_id === 1) {
            setNewVehicle(vehicle);
          } else if (vehicle.vehicle_status_id === 2) {
            setTradeVehicles((prevTradeVehicles) => {
              return [...prevTradeVehicles, vehicle];
            });
          }
        });
      }
    },
  });

  const getVehicleDetails = (vehicle) => {
    if (vehicle.year || vehicle.make || vehicle.model) {
      return `${vehicle.year || ""} ${vehicle.make || ""} ${
        vehicle.model || ""
      }`;
    } else {
      return "N/A";
    }
  };

  const artifact = verification.tradeQuote?.find(
    (data) => data.__typename === "TradeArtifact"
  );
  const videoUrl = {
    english_url: artifact?.ai_url?.english_url,
    spanish_url: artifact?.ai_url?.spanish_url,
  };

  const hasMessage = verification.aiData.spanish
    ? !!artifact?.es_text
    : !!artifact?.text;

  return (
    <VerificationLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          justifyContent: "space-between",
        }}
      >
        {!verification?.aiData?.isAI || !verification?.aiData?.spokeperson ? (
          <Box
            sx={{
              display: "flex",
              p: "3%",
              flexDirection: "column",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              alignItems: "center",
              gap: "2%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                height: "10%",
              }}
            >
              <ToggleComponent isClose={false} />
            </Box>

            {artifact?.text || artifact?.es_text ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  height: "90%",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {!loading && tradeVehicles.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-evenly",
                      alignItems: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <TextUnAvailable
                      text={artifact?.text}
                      es_text={artifact?.es_text}
                    />
                    {hasMessage && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          overflow: "auto",
                        }}
                      >
                        <FormGroup>
                          <Grid container spacing={2}>
                            {tradeVehicles.map((vehicle) => {
                              const isSelected = tradeVehicleIds.includes(
                                parseInt(vehicle.vehicle_id)
                              );

                              return (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  key={vehicle.vehicle_id}
                                >
                                  <Button
                                    fullWidth
                                    variant={
                                      isSelected ? "contained" : "outlined"
                                    }
                                    color="primary"
                                    sx={{
                                      justifyContent: "center",
                                      alignItems: "center",
                                      borderRadius: "8px",
                                      // width:
                                      //   window.innerWidth <= 750 ? "0%" : null,
                                    }}
                                    onClick={() => {
                                      if (isSelected) {
                                        setTradeVehicleIds(
                                          tradeVehicleIds.filter(
                                            (id) =>
                                              id !==
                                              parseInt(vehicle.vehicle_id)
                                          )
                                        );
                                      } else {
                                        setTradeVehicleIds([
                                          ...tradeVehicleIds,
                                          parseInt(vehicle.vehicle_id),
                                        ]);
                                      }
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                      }}
                                    >
                                      <Typography sx={{ fontWeight: "bold" }}>
                                        {getVehicleDetails(vehicle)}
                                      </Typography>
                                      <Typography variant="body2">
                                        {vehicle.vin ? vehicle.vin : "N/A"}
                                      </Typography>
                                    </Box>
                                  </Button>
                                </Grid>
                              );
                            })}

                            <Grid item xs={12} sm={6}>
                              <Button
                                fullWidth
                                variant={
                                  tradeVehicleIds.length === 0
                                    ? "contained"
                                    : "outlined"
                                }
                                color="primary"
                                sx={{
                                  // borderRadius: "8px",
                                  borderRadius: "8px",
                                  textTransform: "none",
                                  height: "55px",
                                }}
                                onClick={() => setTradeVehicleIds([])}
                              >
                                {!verification?.aiData?.spanish
                                  ? "No Vehicle being traded"
                                  : "No se intercambia ningún vehículo."}
                              </Button>
                            </Grid>
                          </Grid>
                        </FormGroup>
                      </Box>
                    )}
                  </Box>
                ) : !loading && tradeVehicles.length <= 0 ? (
                  <Box
                    sx={{
                      width: "100%",
                      padding: "48px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Lottie
                      options={lottieLoopOptions}
                      height={
                        window.innerHeight >= 1440
                          ? 360
                          : window.innerHeight >= 1050
                          ? 360
                          : window.innerHeight >= 900
                          ? 300
                          : 200
                      }
                      width={
                        window.innerHeight >= 1440
                          ? 360
                          : window.innerHeight >= 1050
                          ? 360
                          : window.innerHeight >= 900
                          ? 300
                          : 200
                      }
                    />
                    <Typography variant="h5">
                      No vehicles found to trade
                    </Typography>
                  </Box>
                ) : (
                  <BulletListShimmer
                    size={{
                      height: window.innerWidth <= 750 ? 140 : 200,
                      width: window.innerWidth <= 750 ? 30 : 50,
                    }}
                  />
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-evenly",
                  alignItems: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <TextUnAvailable
                  text={"No artifacts available"}
                  es_text={"No hay artefactos disponibles."}
                />
              </Box>
            )}
          </Box>
        ) : (
          <>
            {((videoUrl.english_url && !verification?.aiData?.spanish) ||
              (videoUrl.spanish_url && verification?.aiData?.spanish)) && (
              <FetchAIVideoUrl
                videoUrl={videoUrl}
                currentIndex={0}
                pagination={false}
                isAI={true}
                animateIn={animateIn}
                setAnimateIn={setAnimateIn}
                duration={duration}
                setDuration={setDuration}
                direction={direction}
                isClose={false}
                showSurveyButton={showSurveyButton}
                setShowSurveyButton={setShowSurveyButton}
                trade={true}
                artifact={artifact}
                loading={loading}
                tradeVehicleIds={tradeVehicleIds}
                setTradeVehicleIds={setTradeVehicleIds}
                tradeVehicles={tradeVehicles}
                getVehicleDetails={getVehicleDetails}
              />
            )}
          </>
        )}

        {/* ✅ Next button */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <AppButton
            buttonText="Next"
            size="medium"
            loading={submitLoading}
            sx={{ width: "50%" }}
            clickHandler={() =>
              submitVehicleTrade({
                variables: {
                  tradeInput: {
                    dealership_id: verification.dealershipId,
                    employee_user_id: parseInt(verification.employeeId),
                    user_id: verification.customer.userId,
                    trade_vehicle_ids: tradeVehicleIds,
                    new_vehicle_id: parseInt(newVehicle.vehicle_id),
                    d_startDate:
                      app.fetchData.d_startDate ||
                      new Date(firstOfMonth).toUTCString(),
                    d_endDate: app.fetchData.d_endDate || today,
                    f_startDate:
                      app.fetchData.f_startDate ||
                      new Date(firstOfMonth).toUTCString(),
                    f_endDate: app.fetchData.f_endDate || today,
                    pageCount: app.fetchData.pageCount || 0,
                    rowsPerPage: app.fetchData.rowsPerPage || 25,
                    search_employee: app.fetchData.searchEmployee || "",
                    search_customer: app.fetchData.searchCustomer || "",
                    isEmployeeOnly: false,
                    timezone: new Date().getTimezoneOffset(),
                    insurance_id: verification?.customer?.insurance_id,
                  },
                },
              })
            }
            disabled={submitLoading}
          />
        </Box>
      </Box>
    </VerificationLayout>
  );
};

export default HocPopup(TradeVehicleComponent);
